import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { CreateClaimTabInfo } from '../../../interfaces/create-claim-tab-info'
import { ClaimInformation } from 'app/interfaces/claim-information'
import { UserInfo } from '../../../services/api.service'
import { TranslateModule } from '@ngx-translate/core'
import { FormsModule } from '@angular/forms'
import { provideNativeDateAdapter } from '@angular/material/core'
import { DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common'
import { InvoiceData } from '../../../interfaces/invoice-claim.interface'
import { ClaimItem } from '../../../interfaces/claim.interface'
import { InvoiceSelectionComponent } from '@components/invoice-selection/invoice-selection.component'
import { RelatedFabricator } from '../../../interfaces/partner-store'
import { SelectValue } from '../../../interfaces/select-value.interface'

@Component({
    selector: 'app-claim-material-selection',
    standalone: true,
    imports: [
        TranslateModule,
        FormsModule,
        NgIf,
        NgForOf,
        NgClass,
        DecimalPipe,
        InvoiceSelectionComponent
    ],
    providers: [provideNativeDateAdapter()],
    templateUrl: './claim-material-selection.component.html',
    styleUrl: './claim-material-selection.component.scss'
})
export class ClaimMaterialSelectionComponent implements OnInit, OnChanges{
    @Input() userInfo!: UserInfo
    @Input() claimInformation!: ClaimInformation
    @Input() materialsSelected!: boolean
    @Input() relatedFabricators!: RelatedFabricator[]
    relatedFabricatorsSelectValues: SelectValue[] = []

    @Output() tabInit = new EventEmitter<CreateClaimTabInfo>()
    @Output() updateClaim = new EventEmitter<ClaimInformation>()
    @Output() continueState = new EventEmitter<boolean>()

    initialSelectedInvoices: InvoiceData[];

    constructor(
    ) {
    }

    ngOnInit() {
        this.tabInit.emit({
            currentTab: this.materialsSelected ? 'material-edit-area' : 'material-selection',
            previousTab: this.materialsSelected ? 'material-selection' : 'claim-info',
            nextTab: this.materialsSelected ? 'classification' : 'material-edit-area'
        })

        this.claimInformation.lastModifiedTab = this.materialsSelected ? 'material-edit-area' : 'material-selection'

        this.setInitialSelectedMaterials()

        this.continueState.emit(this.claimInformation.selectedMaterials.length > 0)
    }

    ngOnChanges(changes: SimpleChanges) {
        if(changes['materialsSelected']) {
            if(changes['materialsSelected'].currentValue){
                this.tabInit.emit({
                    currentTab: 'material-edit-area',
                    previousTab: 'material-selection',
                    nextTab: 'classification'
                })
                this.claimInformation.lastModifiedTab = 'material-edit-area'
                this.canContinue()
            } else {
                this.tabInit.emit({
                    currentTab: 'material-selection',
                    previousTab: 'claim-info',
                    nextTab: 'material-edit-area'
                })
                this.claimInformation.lastModifiedTab = 'material-selection'
                this.setInitialSelectedMaterials()
            }
        }

        if(changes['relatedFabricators']) {
            this.relatedFabricatorsSelectValues =this.relatedFabricators ?
                this.relatedFabricators.filter(fab => fab.id && fab.name).map(fab => ({id: fab.id!, value: fab.id!, label: fab.name!})) :
                []
        }
    }

    onColorSelect($event: InvoiceData[]) {
        this.claimInformation.selectedMaterials = []
        this.claimInformation.cleanupSelectedMaterials = true

        for(const invoice of $event) {
            const claimItem: ClaimItem = {
                productName: invoice.colorName!,
                productBrand: invoice.brand!,
                productFinish: invoice.finish!,
                productThickness: invoice.thickness!,
                productFormat: invoice.format!,
                product: {
                    brandId: null,
                    code: null,
                    colorDesc: null,
                    finishId: null,
                    formulacionColor: null,
                    pictureURL: null,
                    productColorId: null,
                    subBrand: null,
                    thicknessId: null
                },
                barCode: invoice.lotId!,
                image: invoice.imageUrl!,
            }

            this.claimInformation.selectedMaterials.push({
                claimItem: claimItem,
                invoiceData: invoice,
                totalQty: invoice.totalUnits!,
                selectedQty: invoice.availableUnits ? invoice.availableUnits : invoice.totalUnits!
            });
        }
        this.updateClaim.emit(this.claimInformation)
        this.continueState.emit(this.claimInformation.selectedMaterials.length > 0)
    }

    activateEditingSize(selectedMat: { isEditing?: boolean }) {
        selectedMat.isEditing = true
    }

    acceptNewSize(selectedMat: { isEditing?: boolean }) {
        selectedMat.isEditing = false
        this.canContinue()
    }

    canContinue() {
        let canContinue = true
        if(this.claimInformation.selectedMaterials.length === 0) {
            canContinue = false
        }
        // for(const material of this.claimInformation.selectedMaterials) {
        //     if(!material.selectedQty){
        //         canContinue = false
        //         break
        //     }
        // }
        return this.continueState.emit(canContinue)
    }

    brandStringToImage(brand?: string | null){
        if(!brand){
            return;
        }
        switch (brand.toLowerCase()){
            case 'silestone':
                return 'assets/icons/brands/silestone-alt.svg';
            case 'dekton':
                return 'assets/icons/brands/dekton-alt.svg';
            case 'sensa':
                return 'assets/icons/brands/sensa-alt.svg';
            case 'scalea':
                return 'assets/icons/brands/scalea-alt.svg';
        }

        return;
    }

    private setInitialSelectedMaterials() {
        if(this.claimInformation.selectedMaterials){
            this.initialSelectedInvoices = []
            for(const selectedMat of this.claimInformation.selectedMaterials) {
                if(selectedMat.invoiceData){
                    const colors: { [key: string]: string } = {}
                    selectedMat.invoiceData.materialId = selectedMat.invoiceData.materialId?.replace(/^0+/, '') || selectedMat.invoiceData.materialId
                    selectedMat.invoiceData.id = selectedMat.invoiceData.invoiceId + '-' + selectedMat.invoiceData.position

                    if(selectedMat.invoiceData.colorCode && selectedMat.invoiceData.colorName) {
                        colors[selectedMat.invoiceData.colorCode] = selectedMat.invoiceData.colorName!
                    }

                    if(selectedMat.invoiceData){
                        this.initialSelectedInvoices.push(selectedMat.invoiceData)
                    }
                }
            }
        }
    }

    parseMinMaxValue($event: number | null, selectedMat: {selectedQty?: number; totalQty?: number}) {
        if(!$event){
            selectedMat.selectedQty = 0
            return;
        }
        if(!selectedMat.totalQty) {
            selectedMat.selectedQty = 0
            return;
        }
        if($event > selectedMat.totalQty){
            selectedMat.selectedQty = selectedMat.totalQty
        } else if ($event < 0){
            selectedMat.selectedQty = 0
        } else {
            selectedMat.selectedQty = $event;
        }
    }
}
